import { HiArrowLeft, HiExclamationCircle } from "react-icons/hi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import usePageTitle from "../../../hooks/use-page-title";
import FGRichTextEditor from "../../../components/common/FGRichTextEditor";
import { ROUTES } from "../../../routes";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ApiSubmissionDate from "../../../modules/api/submission-date";
import {
  SUBMISSION_STATUS_DRAFT,
  SUBMISSION_STATUS_PUBLISH,
  SUBMISSION_WL,
} from "../../../config/constants";
import ModuleDate from "../../../modules/date";
import Debouncer from "../../../modules/debouncer/debouncer";
import ModuleValidation from "../../../modules/validation";
import Notifier from "../../../modules/notifier/notifier";
import AppUserContext from "../../../context/app-user-context";
import ApiSubmission from "../../../modules/api/submission";
import ModulePopup from "../../../modules/popup/popup";
import Utils from "../../../utils/utils";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import { WLField } from "../../../components/dumb/WLFieldsEditor";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import ApiARIAAnalyze from "../../../modules/api/aria-analyze";
import htmlToMarkdown from "@wcj/html-to-markdown";
import ARIAChat from "../../../components/portal/ARIAChat";
import ApiARIAChat from "../../../modules/api/aria-chat";
import AriaHelper from "../../../modules/aria-helper";
import StaticSubmissionInfo from "../../../components/static/StaticSubmissionInfo";
import ChangeAlias from "../../../components/portal/ChangeAlias";

export default function PageWLEdit() {
  const title = "Edit Workplace Learning Report";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  useEffect(() => {
    setCustomText(title);
  }, []);
  const user = useContext(AppUserContext);
  const navigate = useNavigate();
  const { submissionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submissionFound, setSubmissionFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fields, setFields] = useState<WLField[]>([]);
  const [dateList, setDateList] = useState<any[]>([]);
  const [isDateAvailable, setIsDateAvailable] = useState(false);
  const [ariaHeight, setAriaHeight] = useState("90vh");
  const [ariaSuggestions, setAriaSuggestions] = useState<any[]>([]);
  const [ariaBottomSuggestions, setAriaBottomSuggestions] = useState<any[]>([]);
  const [formattedReport, setFormattedReport] = useState<string>("");
  const [formattedReportObject, setFormattedReportObject] = useState<Record<string, string>>({});
  const [hasAriaReceivedFirstMessage, setHasAriaReceivedFirstMessage] = useState<boolean>(false);
  const [autoAriaReportAnalysis, setAutoAriaReportAnalysis] = useState<boolean>(false);
  const [showAriaRatingModal, setShowAriaRatingModal] = useState<boolean>(true);
  const [ariaProcessingModalOpen, setAriaProcessingModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    submissionDateId: "",
    summaryKeyActivities: "",
    detailsLearning: "",
    reflection: "",
    content: {},
  });

  const submissionDateParsed = useMemo(() => {
    return ModuleDate.parseSubmissionDateFromList(formData?.submissionDateId, dateList);
  }, [dateList, formData]);

  const handleFormChange = useCallback((e) => {
    setFormData((f) => ({
      ...f,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleFieldChange = useCallback((e) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      content: { ...prevFormData.content, [e.target.name]: e.target.value || "" },
    }));
  }, [fields]);

  const trackSectionUpdates = useCallback((currentFormData, previousFormData = null, isFirstMessage = false) => {
    const updates = {};

    if (fields.length > 0) {
      // if (isFirstMessage) {
      //   fields.forEach(field => {
      //     updates[field.name] = true;
      //   });
      //   return updates;
      // }

      fields.forEach(field => {
        const fieldId = field._id;
        const fieldName = field.name;

        if (previousFormData) {
          const oldContent = (previousFormData.content?.[fieldId] || '').trim();
          const newContent = (currentFormData.content?.[fieldId] || '').trim();
          updates[fieldName] = oldContent !== newContent && newContent !== '';
        } else {
          updates[fieldName] = !!currentFormData.content?.[fieldId]?.trim();
        }
      });
    } else {
      // Default fields
      // if (isFirstMessage) {
      //   updates["Summary of Key Activities"] = true;
      //   updates["Details of Learning"] = true;
      //   updates["Reflection"] = true;
      //   return updates;
      // }

      const oldSummary = previousFormData ? (previousFormData.summaryKeyActivities || '').trim() : '';
      const oldDetails = previousFormData ? (previousFormData.detailsLearning || '').trim() : '';
      const oldReflection = previousFormData ? (previousFormData.reflection || '').trim() : '';

      const newSummary = (currentFormData.summaryKeyActivities || '').trim();
      const newDetails = (currentFormData.detailsLearning || '').trim();
      const newReflection = (currentFormData.reflection || '').trim();

      updates["Summary of Key Activities"] = previousFormData ?
        (oldSummary !== newSummary && newSummary !== '') :
        !!newSummary;

      updates["Details of Learning"] = previousFormData ?
        (oldDetails !== newDetails && newDetails !== '') :
        !!newDetails;

      updates["Reflection"] = previousFormData ?
        (oldReflection !== newReflection && newReflection !== '') :
        !!newReflection;
    }

    return updates;
  }, [fields]);

  const handleFormSubmit = (submissionStatus: string) => {
    if (submitting) return;
    setSubmitting(true);

    const IS_DRAFT = submissionStatus === SUBMISSION_STATUS_DRAFT;

    Debouncer.execute("UPDATE_WL", async () => {
      const validation = ModuleValidation.isSubmissionWLValid(formData, fields);

      // validation only applied to non-draft mode
      if (!validation.valid && !IS_DRAFT) {
        setSubmitting(false);
        return ModulePopup.showAlert({
          title: (
            <>
              <HiExclamationCircle /> Alert!
            </>
          ),
          // description: validation.message,
          description: <>
            <div>Please complete the Report before you submit!</div>
            {validation.emptyFields.length > 0 && <div className="mt-2 mb-0">
              Required field(s): {validation.emptyFields.map((v, i) => <React.Fragment key={i}>
                <strong>{v.name}</strong>
                {i < validation.emptyFields.length - 1 && <>, </>}
              </React.Fragment>)}
            </div>}
          </>,
        });
      }

      const result = await ApiSubmission.wlUpdate(submissionId, {
        modifiedUserId: user.id,
        status: submissionStatus,
        studentId: user.referenceUserId,
        ...formData,
      });

      if (result.status) {
        IS_DRAFT
          ? Notifier.success("Draft saved")
          : Notifier.success(result.message);

        if (submissionStatus === SUBMISSION_STATUS_PUBLISH) {
          try {
            // submit to aria to analyze
            const selectedDate = dateList.find(date => date.value === formData.submissionDateId);
            const deadlineDateStart = selectedDate ? new Date(selectedDate.fromMs).toISOString() : "";
            const deadlineDateEnd = selectedDate ? new Date(selectedDate.toMs).toISOString() : "";

            // 2025-08-19
            // client doesn't want to block user's experience by showing ARIA
            // processing dialog. 🤞
            (async () => {
              // setAriaProcessingModalOpen(true);

              // Submit to ARIA for analysis
              const ariaAnalysis = await ApiARIAAnalyze.analyzeStudentReportForStudent(
                user.id,
                submissionId,
                formattedReport,
                deadlineDateStart,
                deadlineDateEnd,
                getFieldsDescriptions(),
                AriaHelper.getSectionsWordLimit(fields),
                formattedReportObject
              );

              if (!ariaAnalysis.status) {
                // Notifier.info("Report submitted but ARIA analysis failed: " + ariaAnalysis.message);
              }

              // setAriaProcessingModalOpen(false);
            })();

            /**
             * @readme
             * If ARIA has received the first message,
             * it means the user has interacted with ARIA chat box at least one time.
             * If the user has rated ARIA before (checked by showAriaRatingModal)
             * then we show the rating modal.
             * If not, we just navigate to the report page after a short delay.
             */
            if (hasAriaReceivedFirstMessage && showAriaRatingModal) {
              ModulePopup.showFeedbackAlert({
                title: "Report Submitted!",
                description: "Thanks for submitting! How would you rate your experience with ARIA?",
                buttonLabel: "Send Feedback",
                onSubmit: async (feedback) => {
                  const rateAriaChat = await ApiARIAChat.rateARIAChat(user.id, submissionId, feedback.comment, feedback.isLiked, user.role);
                  if (rateAriaChat.status) {
                    Notifier.success("Feedback submitted. Thank you!");
                  } else {
                    Notifier.error("An error occurred");
                  }
                  navigate(ROUTES.PORTAL_WORKPLACE_LEARNING);
                },
                onHome: () => {
                  navigate(ROUTES.PORTAL_DASHBOARD);
                },
                onClose: () => {
                  navigate(ROUTES.PORTAL_WORKPLACE_LEARNING);
                }
              });
            } else {
              await Utils.sleep(2000);
              navigate(ROUTES.PORTAL_WORKPLACE_LEARNING);
            }
          } catch (error) {
            setAriaProcessingModalOpen(false);
            Notifier.error("An error occurred during report analysis.");
            navigate(ROUTES.PORTAL_WORKPLACE_LEARNING);
          }
        }
      } else {
        Notifier.error(result.message);
      }

      setSubmitting(false);
    });
  };

  const convertToMarkdown = async (html: string) => {
    let content = await htmlToMarkdown({ html });
    content = content.replace(/^# /gm, '## '); // Convert H1 (`# `) to H2 (`## `) in content only
    return content;
  };

  const formatReportContent = async (formData: any) => {
    let markdownContent = '';
    const sections: Record<string, string> = {};

    if (formData.submissionDateId && fields.length > 0) {
      const fieldContents = await Promise.all(
        fields.map(async (field) => {
          const content = formData.content?.[field._id] || '';
          const markdown = content ? await convertToMarkdown(content as string) : "";
          sections[field.name] = markdown || "";
          return `# ${field.name}\n\n${markdown}`;
        })
      );
      markdownContent = fieldContents.join('\n\n');
    } else {
      const summary = formData.summaryKeyActivities
        ? await convertToMarkdown(formData.summaryKeyActivities)
        : "";
      const details = formData.detailsLearning
        ? await convertToMarkdown(formData.detailsLearning)
        : "";
      const reflection = formData.reflection
        ? await convertToMarkdown(formData.reflection)
        : "";

      sections["Summary of Key Activities"] = summary || "";
      sections["Details of Learning"] = details || "";
      sections["Reflection"] = reflection || "";
      markdownContent = `# Summary of Key Activities\n\n${summary}\n\n` +
                        `# Details of Learning\n\n${details}\n\n` +
                        `# Reflection\n\n${reflection}`;
    }

    return {
      content: markdownContent,
      sections: sections,
    };
  };

  const getFieldsDescriptions = (): { [key: string]: string } => {
    if (fields.length > 0) {
      return fields.reduce((acc, field) => {
        acc[field.name.replace(/\s+/g, '')] = field.description || '';
        return acc;
      }, {} as { [key: string]: string });
    } else {
      return {
        summaryKeyActivities: '',
        detailsLearning: '',
        reflection: ''
      };
    }
  };

  const setSubmissionDate = async (submissionDate) => {
    if (!user.activeCohort._id || !user.activeCohort._id.trim()) return;

    const { cohort, result } =
      await ApiSubmissionDate.getSubmissionDateListForSubmission(
        user.activeCohort._id,
        SUBMISSION_WL,
        submissionDate
      );
    if (Array.isArray(cohort?.wlFormat?.fields)) {
      setFields(AriaHelper.checkWordLimitOnFields(cohort.wlFormat.fields));
    }
    setDateList(result);
  };

  const getSubmission = async () => {
    const res = await ApiSubmission.getSubmission(submissionId);

    if (!res.status) {
      setSubmissionFound(false);
    }

    const data = res.data;

    if (data.studentId !== user.referenceUserId) {
      setError("You are not allowed to edit this submission.");
      setSubmissionFound(true);
      return;
    }
    // Determine if the report is editable based on the status and date
    // Note: The original code had a condition that checked if the report was before the submission due date.
    const status = data.status;
    // const isEditMode =
    //   status === SUBMISSION_STATUS_DRAFT ||
    //   (status === SUBMISSION_STATUS_PUBLISH &&
    //     ModuleDate.isBeforeSubmissionDue(data.submissionDate.to));
    // currently, we only allow editing of draft reports only
    const isEditMode = status === SUBMISSION_STATUS_DRAFT;

    if (!isEditMode) {
      navigate(ROUTES.PORTAL_WORKPLACE_LEARNING);
      setError("You can't edit this past submission.");
      setSubmissionFound(true);
      return;
    }

    await setSubmissionDate(data.submissionDate);
    setFormData({
      submissionDateId: data.submissionDate._id,
      summaryKeyActivities: data.summaryKeyActivities,
      detailsLearning: data.detailsLearning,
      reflection: data.reflection,
      content: data.content ? JSON.parse(data.content) : {},
    });
    setIsDateAvailable(true);
    setSubmissionFound(true);
  };

  const prepareSectionUpdateForChatAnalysis = useCallback((isFirstMessage?: boolean) => {
    const previousFormState = JSON.parse(sessionStorage.getItem('previousReportState')) || null;
    const newUpdateStatus = trackSectionUpdates(formData, previousFormState, isFirstMessage);
    sessionStorage.setItem('previousReportState', JSON.stringify(formData));
    return newUpdateStatus;
  }, [formData, trackSectionUpdates]);

  useEffect(() => {
    (async () => {
      await getSubmission();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setAriaHeight("105vh");
      } else {
        setAriaHeight("90vh");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // let suggestions = [`Hey ${user.name}! Have something to discuss with me?`];

    // if (fields.length === 1) {
    //   suggestions.push(`Can you guide me to fill in the ${fields[0].name} section?`);
    // } else if (fields.length >= 2) {
    //   suggestions.push(`Can you guide me to fill in the ${fields[0].name} section?`);
    //   suggestions.push(`Can you guide me to fill in the ${fields[1].name} section?`);
    // } else {
    //   suggestions.push("Can you guide me to fill in the Summary of Key Activities section?");
    //   suggestions.push("Can you guide me to fill in the Details of Learning section?");
    // }
    // suggestions.push("Does my report look good?");

    let suggestions = [
      `You can choose any of the two buttons below to start a conversation, or ask ARIA any question!

(Click in the Report's Section **before** interacting with ARIA)`,
      {
        text: "Guide me in drafting the Report",
        description: "(for the Section you are currently drafting)"
      },
      {
        text: "Give feedback on my Report",
        description: "(for the Sections you have completed)"
      }
    ];

    setAriaSuggestions(suggestions);

    // setAriaBottomSuggestions([
    //   "Can you guide me in drafting this Report?",
    //   "Can you give me feedback on what I've drafted in my Report so far?"
    // ]);
  }, [fields, user.name, loading]);

  useEffect(() => {
    const generateFormattedReport = async () => {
      if (submissionFound && formData) {
        const reportData = await formatReportContent(formData);
        setFormattedReport(reportData.content);
        setFormattedReportObject(reportData.sections);
      }
    };

    generateFormattedReport();
  }, [submissionFound, formData, fields, trackSectionUpdates]);

  useEffect(() => {
    const getSubmissionRating = async () => {
      if (submissionFound) {
        const ariaRating = await ApiARIAChat.getAriaRating(user.id, submissionId);
        if (ariaRating.status) setShowAriaRatingModal(false);
      }
    }
    getSubmissionRating();
  }, [submissionFound]);

  useEffect(() => {
    if (ariaProcessingModalOpen) {
      ModulePopup.showAlert({
        title: "Processing Submission",
        description: "Please don't close this popup as ARIA is processing your submission.",
        isCloseable: false,
      })
    } else {
      ModulePopup.hideAlert();
    }
  }, [ariaProcessingModalOpen]);

  useEffect(() => {
    if (submissionFound && formData.submissionDateId) {
      sessionStorage.removeItem('previousReportState');

      const initialFormData = {
        submissionDateId: formData.submissionDateId,
        summaryKeyActivities: formData.summaryKeyActivities,
        detailsLearning: formData.detailsLearning,
        reflection: formData.reflection,
        content: formData.content || {},
      };
      sessionStorage.setItem('previousReportState', JSON.stringify(initialFormData));
    }
  }, [submissionFound, formData.submissionDateId]);

  return (
    <main
      className="content-generic content-generic--fluid row sm-block"
      data-comp="PageWLEdit"
    >
      <div className="col-lg-8 col-md-6">
        {/* <div className="d-flex gap-2 hide-mobile-down"> */}
          {/* <h1 className="fs-title"> */}
            {/* <Link
              to={ROUTES.PORTAL_WORKPLACE_LEARNING}
              className="text-primary p-0 me-2"
              title="Go back"
            >
              <HiArrowLeft />
            </Link> */}
            {/* {title} */}
          {/* </h1> */}
        {/* </div> */}

        {loading && <p className="alert alert-info">Loading. Please wait...</p>}

        {!loading && !submissionFound && (
          <p className="alert alert-info">Submission not found.</p>
        )}

        {!loading && submissionFound && error.length > 0 && (
          <p className="alert alert-danger">{error}</p>
        )}

        {!loading && submissionFound && error === "" && (
          <form onSubmit={(e) => e.preventDefault()}>
            {false && <FGDropdownApi
              label="Select submission date"
              name="submissionDateId"
              variant="normal"
              options={dateList}
              defaultValue={formData.submissionDateId}
              onChange={handleFormChange}
              disabled
            />}

            {submissionDateParsed.found && <p>
              <strong>Workplace Learning Report for {submissionDateParsed.startDate} (Deadline: {submissionDateParsed.endDate})</strong>
            </p>}

            {!loading && formData.submissionDateId === "" && (
              <p className="alert alert-warning">
                Please select the submission date.
              </p>
            )}

            {!loading && isDateAvailable && (
              <>
                <div className="form-report mt-3">
                  {fields.length ? (
                    fields.map((field, i) => (
                      <FGRichTextEditor
                        limitWords={field.max_word}
                        key={`wl-field-${i}`}
                        variant="normal"
                        label={<>
                          <strong>{field.name}</strong>
                          <span className="d-none">{parseInt(field?.max_word as any, 10) > 0 ? ` (up to ${field?.max_word} words)` : ""}</span>
                        </>}
                        desc={field.description}
                        name={field._id}
                        value={formData.content[field._id]}
                        onChange={handleFieldChange}
                      />
                    ))
                  ) : (
                    <>
                      <FGRichTextEditor
                        variant="normal"
                        label={<strong>Summary of Key Activities</strong>}
                        name="summaryKeyActivities"
                        value={formData.summaryKeyActivities}
                        onChange={handleFormChange}
                      />

                      <hr />

                      <FGRichTextEditor
                        variant="normal"
                        label={<strong>Details of Learning</strong>}
                        name="detailsLearning"
                        value={formData.detailsLearning}
                        onChange={handleFormChange}
                      />

                      <hr />

                      <FGRichTextEditor
                        variant="normal"
                        label={<strong>Reflection</strong>}
                        name="reflection"
                        value={formData.reflection}
                        onChange={handleFormChange}
                      />
                    </>
                  )}
                </div>

                <StaticSubmissionInfo />

                <div className="my-3">
                  <button
                    type="button"
                    className="mt-1 btn btn-secondary btn-mw-centered"
                    disabled={submitting}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!hasAriaReceivedFirstMessage) {
                        ModulePopup.showAlertConfirm({
                          title: "Before you Submit...",
                          description: (
                            <>
                              Would you like to consult ARIA for feedback before submitting your Workplace Learning Report?
                            </>
                          ),
                          labelYes:"Yes",
                          onYes() {
                            // submit aria message for auto report analysis
                            setAutoAriaReportAnalysis(true);
                          },
                          labelNo:"No",
                          onNo() {
                            handleFormSubmit(SUBMISSION_STATUS_PUBLISH);
                          }
                        });
                      } else {
                        ModulePopup.showAlertConfirm({
                          title: "Confirmation",
                          description: "Are you sure you want to submit?",
                          labelYes:"Yes",
                          onYes() {
                            handleFormSubmit(SUBMISSION_STATUS_PUBLISH);
                          },
                          labelNo:"No",
                          onNo() {
                            // do nothing
                          }
                        });
                      }
                    }}
                  >
                    Submit Report
                  </button>{" "}
                  <button
                    type="button"
                    className="mt-1 btn btn-outline-secondary btn-mw-centered"
                    disabled={submitting}
                    onClick={(e) => {
                      e.preventDefault();
                      handleFormSubmit(SUBMISSION_STATUS_DRAFT);
                    }}
                  >
                    Save Draft
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {!loading && submissionFound && <ChangeAlias showOnOnlyEmpty={true} showDescription={true} />}
      </div>

      {!loading && (
        <div className="col-lg-4 col-md-6">
          {/* <h1 className="fs-title d-none d-md-block">Ask ARIA</h1> */}
          <div
            className="mobile-height-collapse"
            style={{
              position: "sticky",
              top: 10,
              height: ariaHeight,
              zIndex: "100",
              transition: "height 0.3s ease-in-out",
            }}
          >
            <ARIAChat
              custom_title="Hi, I'm ARIA, your IWSP Coach"
              suggestions={ariaSuggestions}
              bottomSuggestionButtons={ariaBottomSuggestions}
              is_sticky={true}
              is_api={true}
              studentId={user.id}
              report={formattedReportObject}
              prepareSectionUpdateForChatAnalysis={prepareSectionUpdateForChatAnalysis}
              has_aria_received_first_message={hasAriaReceivedFirstMessage}
              setHasAriaReceivedFirstMessage={setHasAriaReceivedFirstMessage}
              auto_report_analysis={autoAriaReportAnalysis}
              setAutoAriaReportAnalysis={setAutoAriaReportAnalysis}
              hide_close_on_desktop={true}
              sectionDescription={getFieldsDescriptions()}
              sectionMaxWord={AriaHelper.getSectionsWordLimit(fields)}
            />
          </div>
        </div>
      )}

      <FooterBoxAction backUrl={ROUTES.PORTAL_WORKPLACE_LEARNING}/>
    </main>
  );
}
